import { Authorization, Token, UserInfo } from '@/constants/authorization';
import { getSearchValue } from './common-util';
const KeySet = [Authorization, Token, UserInfo];
export const OAuthAuthQueryParam = 'auth';
export const EmbedAuthQueryParam = 'embed_auth';
// Legacy embed links use `auth`, but only public share/widget/document pages
// should be allowed to consume it as an embed token.
const legacyEmbedRoutes = [
  '/next-chats/share',
  '/next-chats/widget',
  '/agent/share',
  '/next-search/share',
  '/document/',
];

const storage = {
  getAuthorization: () => {
    return localStorage.getItem(Authorization);
  },
  getToken: () => {
    return localStorage.getItem(Token);
  },
  getUserInfo: () => {
    return localStorage.getItem(UserInfo);
  },
  getUserInfoObject: () => {
    const userInfoStr = localStorage.getItem(UserInfo);
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  },
  setAuthorization: (value: string) => {
    localStorage.setItem(Authorization, value);
  },
  setToken: (value: string) => {
    localStorage.setItem(Token, value);
  },
  setUserInfo: (value: string | Record<string, unknown>) => {
    const valueStr = typeof value !== 'string' ? JSON.stringify(value) : value;
    localStorage.setItem(UserInfo, valueStr);
  },
  setItems: (pairs: Record<string, string>) => {
    Object.entries(pairs).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  },
  removeAuthorization: () => {
    localStorage.removeItem(Authorization);
  },
  removeAll: () => {
    KeySet.forEach((x) => {
      localStorage.removeItem(x);
    });
  },
  setLanguage: (lng: string) => {
    localStorage.setItem('lng', lng);
  },
  getLanguage: (): string => {
    return localStorage.getItem('lng') as string;
  },
};

export const getStoredAuthorization = () => {
  return storage.getAuthorization() || '';
};

export const getAuthorization = () => {
  const embedAuth = getEmbedAuthorizationToken();
  const authorization = embedAuth
    ? 'Bearer ' + embedAuth
    : getStoredAuthorization();

  return authorization;
};

export const isLegacyEmbedAuthRoute = (pathname = window.location.pathname) => {
  return legacyEmbedRoutes.some((route) => pathname.startsWith(route));
};

export const getEmbedAuthorizationToken = () => {
  const auth = getSearchValue(EmbedAuthQueryParam);
  if (auth) {
    return auth;
  }

  // Backward compatibility for already embedded client widgets using `?auth=...`.
  if (isLegacyEmbedAuthRoute()) {
    return getSearchValue(OAuthAuthQueryParam);
  }

  return '';
};

/**
 * Flexar: requests of this page are authorized by an embed token — a public share/widget page.
 * Such a page asks nothing about its viewer: endpoints that answer only a logged-in user
 * (`/v1/user/*`, `/v1/preference/*`) would give a guest a 401 — once it sent them to the login
 * page, now an error toast — and a logged-in viewer their own permissions, settings and theme on a
 * page that is the same for everyone. Hooks of those endpoints stay off here.
 */
export const hasEmbedAuthorization = () => !!getEmbedAuthorizationToken();

/**
 * Flexar: the login token an OAuth callback brought in the URL (`/?auth=<token>`). None on a public
 * share/widget page: there `auth` is the page's embed token, and stored as a login it replaced the
 * session of a visitor already logged in in this browser — the next 401 logged them out.
 */
export const loginTokenFromUrl = (url: URL) =>
  isLegacyEmbedAuthRoute(url.pathname)
    ? null
    : url.searchParams.get(OAuthAuthQueryParam);

export default storage;

// Will not jump to the login page
export function redirectToLogin() {
  // const env = import.meta.env;
  window.location.href = location.origin + `/login`;
}
